/* eslint-disable import/prefer-default-export, max-lines */
import { format } from 'date-fns';
import { isNull, pick, reduce } from 'lodash';

import { withTitleCasedDrugFields } from 'common/utils/titleCase';
import graphqlClient from '../graphql';
import {
  assertQs1RxNumberGql,
  bulkFillGql,
  bulkLabelLotNdcReviewGql,
  bulkOverrideLineItemsGql,
  closeEScriptRequestsGql,
  commitBulkLabelLotNdcGql,
  completeEntryGql,
  controlledSubstancesCheckGql,
  copayResultCheckGql,
  generateBulkLabelsGql,
  getRequestLineItemVerificationDataGql,
  importEScriptRequestsGql,
  labelRegenerationStatusesGql,
  listBulkLabelPrintHistoryGql,
  listBulkLabelsGql,
  listEVisitRequestsGql,
  listLineItemsGql,
  listOrdersForProductFulfillmentGql,
  listRxIntakeRequestsGql,
  markLineItemsPrintedGql,
  prescriberClarificationCheckGql,
  profitabilityResultCheckGql,
  reGenerateLabelGql,
  reviewLineItemGql,
  updateRequestLineItemStateGql,
  verifyRequestLineItemGql,
} from './gql';
import {
  transformBulkLabels,
  transformListLineItems,
  transformListOrdersForProductFulfillment,
  transformListRxIntakeRequests,
} from './transform';

export const BULK_LABEL_PAGE_SIZE = Number(process.env.REACT_APP_BULK_LABEL_PAGE_SIZE) || 50;

export const listLineItemsApi = ({ page, sortBy, filter }) => {
  const transformedFilter = {
    ...filter,
    needsByDate: filter.needsByDate && format(new Date(filter.needsByDate), 'yyyy-MM-dd'),
    shipByDate: filter.shipByDate && format(new Date(filter.shipByDate), 'yyyy-MM-dd'),
    requestedDate: filter.requestedDate && format(new Date(filter.requestedDate), 'yyyy-MM-dd'),
  };

  return graphqlClient
    .request(listLineItemsGql, {
      offset: (page - 1) * 25,
      sortBy,
      filter: reduce(
        // omit keys if values are null
        transformedFilter,
        (result, value, key) => ({
          ...result,
          [key]: isNull(value) ? undefined : value,
        }),
        {}
      ),
    })
    .then(transformListLineItems);
};

export const controlledSubstancesCheckApi = ({ checkInput }) => {
  return graphqlClient
    .request(controlledSubstancesCheckGql, { checkInput })
    .then(({ controlledSubstancesCheck }) => controlledSubstancesCheck);
};

export const reviewLineItemApi = ({
  mpi,
  lineItemId,
  reviewResult,
  reasonForRejection,
  qs1RxNumber,
}) => {
  return graphqlClient
    .request(reviewLineItemGql, {
      mpi,
      lineItemId,
      reviewResult,
      reasonForRejection,
      qs1RxNumber,
    })
    .then(({ reviewLineItem }) => reviewLineItem);
};

export const prescriberClarificationCheckApi = ({ checkInput }) => {
  return graphqlClient
    .request(prescriberClarificationCheckGql, { checkInput })
    .then(({ prescriberClarificationCheck }) => prescriberClarificationCheck);
};

export const copayResultCheckApi = ({ checkInput }) => {
  return graphqlClient
    .request(copayResultCheckGql, { checkInput })
    .then(({ copayResultCheck }) => copayResultCheck);
};

export const profitabilityResultCheckApi = ({ checkInput }) => {
  return graphqlClient
    .request(profitabilityResultCheckGql, { checkInput })
    .then(({ profitabilityResultCheck }) => profitabilityResultCheck);
};

export const getRequestLineItemVerificationDataApi = ({ mpi, requestLineItemId }) => {
  return graphqlClient
    .request(getRequestLineItemVerificationDataGql, { mpi, requestLineItemId })
    .then(({ getRequestLineItemVerificationData }) => getRequestLineItemVerificationData);
};

export const verifyRequestLineItemApi = ({ input }) => {
  return graphqlClient
    .request(verifyRequestLineItemGql, { input })
    .then(({ verifyRequestLineItem }) => verifyRequestLineItem);
};

export const listBulkLabelsApi = ({ page, sortBy, filter, limit = BULK_LABEL_PAGE_SIZE }) => {
  return graphqlClient
    .request(listBulkLabelsGql, {
      limit,
      offset: (page - 1) * limit,
      sortBy,
      filter: reduce(
        filter,
        (result, value, key) => ({
          ...result,
          [key]: isNull(value) ? undefined : value,
        }),
        {}
      ),
    })
    .then(transformBulkLabels);
};

export const generateBulkLabelsApi = ({ requestLineItemIds, filter, isBulk }) => {
  const validFilterKeys = [
    'patientName',
    'medicationNames',
    'lastPrintedDate',
    'needsByDate',
    'isPrinted',
    'lineItemStatus',
    'taggedForReview',
    'partnerIds',
    'practiceIds',
    'fulfillingPharmacyIds',
    'fillType',
    'orderId',
    'states',
    'notInPartnerIds',
    'isReshipment',
    'batchStatus',
    'orderDate',
    'labelDate',
    'createdAt',
    'createdBetween',
    'itemType',
    'requestType',
    'itemCount',
    'tags',
    'tagsNotIn',
    // POM-6330: without it a print-by-filter would fall back to EXACT and print a different set
    // than the queue shows under Contains.
    'tagMatchMode',
    'drugProfileIds',
    'ndcs',
  ];

  const correctedFilter = filter ? pick(filter, validFilterKeys) : {};

  return graphqlClient
    .request(generateBulkLabelsGql, { requestLineItemIds, filter: correctedFilter, isBulk })
    .then(({ generateBulkLabels }) => generateBulkLabels);
};

export const markLineItemsPrintedApi = ({ lineItemIds, markItemsFilled, isBulkAction }) => {
  return graphqlClient
    .request(markLineItemsPrintedGql, {
      lineItemIds,
      markProductsFilled: markItemsFilled,
      isBulkAction,
    })
    .then(({ markLineItemsPrinted }) => markLineItemsPrinted);
};

export const listOrdersForProductFulfillmentApi = ({ page, sortBy, filter }) => {
  const transformedFilter = {
    ...filter,
    needsByDate: filter.needsByDate && format(new Date(filter.needsByDate), 'yyyy-MM-dd'),
    shipByDate: filter.shipByDate && format(new Date(filter.shipByDate), 'yyyy-MM-dd'),
    requestNeedsByDate:
      filter.requestNeedsByDate && format(new Date(filter.requestNeedsByDate), 'yyyy-MM-dd'),
  };

  return graphqlClient
    .request(listOrdersForProductFulfillmentGql, {
      offset: (page - 1) * 25,
      sortBy,
      filter: reduce(
        // omit keys if values are null
        transformedFilter,
        (orders, value, key) => ({
          ...orders,
          [key]: isNull(value) ? undefined : value,
        }),
        {}
      ),
    })
    .then(transformListOrdersForProductFulfillment);
};

export const completeEntryApi = ({ mpi, lineItemId, state }) =>
  graphqlClient
    .request(completeEntryGql, { mpi, lineItemId, state })
    .then(({ completeEntry }) => completeEntry);

export const assertQs1RxNumberApi = ({ mpi, rxNumber, lineItemId }) =>
  graphqlClient
    .request(assertQs1RxNumberGql, { lineItemId, mpi, rxNumber })
    .then(({ assertQs1RxNumber }) => assertQs1RxNumber);

export const updateRequestLineItemStateApi = (input) =>
  graphqlClient
    .request(updateRequestLineItemStateGql, input)
    .then(({ updateRequestLineItemState }) => updateRequestLineItemState);

export const bulkFillApi = ({ lineItemIds }) =>
  graphqlClient.request(bulkFillGql, { lineItemIds }).then(({ bulkFill }) => bulkFill);

export const listEVisitRequestsApi = ({ page, sortBy, filter }) => {
  const transformedFilter = {
    ...filter,
    requestedDate: filter.requestedDate && format(new Date(filter.requestedDate), 'yyyy-MM-dd'),
  };

  return graphqlClient
    .request(listEVisitRequestsGql, {
      offset: (page - 1) * 25,
      sortBy,
      filter: reduce(
        transformedFilter,
        (result, value, key) => ({
          ...result,
          [key]: isNull(value) ? undefined : value,
        }),
        {}
      ),
    })
    .then(({ listAwaitingEVisitRequests }) => listAwaitingEVisitRequests);
};

export const listRxIntakeRequestsApi = ({ page, sortBy, filter }) => {
  const defaultItemsPerPage = Number(process.env.REACT_APP_DEFAULT_ITEMS_PER_PAGE);
  const defaultPageOffset = Number(process.env.REACT_APP_DEFAULT_PAGE_OFFSET);

  return graphqlClient
    .request(listRxIntakeRequestsGql, {
      limit: defaultItemsPerPage,
      offset: (page - 1) * defaultItemsPerPage || defaultPageOffset,
      sortBy,
      filter: reduce(
        filter,
        (result, value, key) => ({
          ...result,
          [key]: isNull(value) ? undefined : value,
        }),
        {}
      ),
    })
    .then(transformListRxIntakeRequests);
};

export const importEScriptRequestsApi = (input) =>
  graphqlClient
    .request(importEScriptRequestsGql, { input })
    .then(({ importEScriptOrders }) => importEScriptOrders);

export const closeEScriptRequestsApi = (eScriptIds) =>
  graphqlClient
    .request(closeEScriptRequestsGql, { eScriptIds })
    .then(({ closeEScriptOrder }) => closeEScriptOrder);

export const regenerateLabelsApi = (requestLineItemIds) =>
  graphqlClient
    .request(reGenerateLabelGql, { requestLineItemIds })
    .then(({ regenerateLabel }) => regenerateLabel);

export const listBulkLabelPrintHistoryApi = ({ page, sortBy, filter }) => {
  return graphqlClient
    .request(listBulkLabelPrintHistoryGql, {
      offset: (page - 1) * 25,
      sortBy,
      filter,
    })
    .then(({ listBulkLabelDownloadHistory }) => listBulkLabelDownloadHistory);
};

// POM-6010: everything the Review Lot & NDC dialog needs for one bulk-print selection.
// reviewSessionId groups everything one popup open does into a single audit trail — see
// useReviewSessionId. Optional on the wire: core drops a malformed id rather than failing the call.
/**
 * POM-6121 — casing for the hand-recorded lot options, applied HERE and nowhere else.
 *
 * `drugName` goes through the same title-caser every other drug string on the screen does, so the
 * panel's NDC options cannot read differently from the grid rows beside them.
 *
 * Three fields are deliberately left exactly as core sent them:
 *  - `ndc` and `lotNumber` are identifiers. Casing them would change what they identify, and a lot
 *    number is matched case-insensitively against the typed value rather than re-cased to fit.
 *  - `manufacturer` is a proper name. Title-casing it turns 'CVS Health' into 'Cvs Health', and
 *    the availability groups rendered in the very same dialog already show it raw -- casing it on
 *    only one of the two would put one manufacturer on screen under two spellings.
 */
const withTitleCasedNdcOption = (option) => ({
  ...option,
  ...(option.drugName ? withTitleCasedDrugFields({ drugName: option.drugName }) : {}),
});

const toRecordableLots = (entry) => ({
  productId: entry.productId,
  fulfillingPharmacyId: entry.fulfillingPharmacyId,
  hasInventoryMaster: !!entry.hasInventoryMaster,
  ndcOptions: (entry.ndcOptions ?? []).map(withTitleCasedNdcOption),
  // Order is core's and is load-bearing: most-recently-moved first, which is the order an
  // operator recognises the lot in their hand by. Explicitly NOT sorted here -- see the comment
  // on the list itself in ChangeNdcLotManualEntry.
  depletedLots: entry.depletedLots ?? [],
});

export const bulkLabelLotNdcReviewApi = ({ requestLineItemIds, reviewSessionId }) =>
  graphqlClient
    .request(bulkLabelLotNdcReviewGql, { requestLineItemIds, reviewSessionId })
    .then(({ bulkLabelLotNdcReview }) => ({
      ...bulkLabelLotNdcReview,
      items: (bulkLabelLotNdcReview.items ?? []).map(withTitleCasedDrugFields),
      availability: bulkLabelLotNdcReview.availability ?? [],
      // Defaulted rather than left undefined so every reader can treat "no entry for this
      // product" as the one meaning: nothing may be hand-recorded here.
      recordableLots: (bulkLabelLotNdcReview.recordableLots ?? []).map(toRecordableLots),
    }));

/**
 * Persists reviewed lot/NDC changes onto their fills. Does NOT allocate stock.
 *
 * The mutation queues the label regeneration for the rows that committed and reports it on
 * `labelRegeneration`. Do NOT call regenerateLabelsApi after this: two runs over the same line
 * items race, and whichever finishes first clears the LABEL_REGENERATING tag while the other is
 * still rendering.
 */
export const commitBulkLabelLotNdcApi = ({
  changes,
  reviewSessionId,
  batchLineItemIds,
  claimShortfallLotIds,
}) =>
  graphqlClient
    .request(commitBulkLabelLotNdcGql, {
      changes,
      reviewSessionId,
      batchLineItemIds,
      // POM-6536 — the lots whose shortfall the operator confirmed claiming. Omitted when none,
      // so a commit without claims sends exactly what it did before.
      ...(claimShortfallLotIds?.length ? { claimShortfallLotIds } : {}),
    })
    .then(({ commitBulkLabelLotNdc }) => commitBulkLabelLotNdc);

/** Cheap enough to poll: which of these line items are still having their label rebuilt. */
export const labelRegenerationStatusesApi = (requestLineItemIds) =>
  graphqlClient
    .request(labelRegenerationStatusesGql, { requestLineItemIds })
    .then(({ labelRegenerationStatuses }) => labelRegenerationStatuses);
export const bulkOverrideOperation = {
  NDC_UPDATE: 'NDC_UPDATE',
  /* The wire value is PRODUCT_UPDATE, which is what core's BulkOverrideOperationType carries
   * (POM-6029). The key stays PRODUCT_ID_UPDATE so the screen's own references are untouched. */
  PRODUCT_ID_UPDATE: 'PRODUCT_UPDATE',
  NEEDS_REVIEW_UPDATE: 'NEEDS_REVIEW_UPDATE',
  UNFULFILLMENT_UPDATE: 'UNFULFILLMENT_UPDATE',
  QUANTITY_UPDATE: 'QUANTITY_UPDATE',
  /* POM-6167. Named for the feature rather than with the _UPDATE suffix the other five carry,
   * because this one does not update anything on the order in front of you -- it reduces the
   * original and schedules the remainder as a new order (POM-6150). Matches what core already
   * calls this everywhere else: the fill_later_item table, FillLaterItemStatus, and the
   * FILL_LATER_ORDER_CREATED / FILL_LATER_SPLIT_CREATED_FROM_ORDER events.
   *
   * Carried on this mutation so it inherits the chunking and the queued-progress path rather than
   * rebuilding both; if the team decides a split does not belong on an "override" mutation, only
   * this branch and its gql move. */
  FILL_LATER_SPLIT: 'FILL_LATER_SPLIT',
  FILL_LATER_MOVE: 'FILL_LATER_MOVE',
};

export const bulkOverrideLineItemsApi = ({ operationType, productId, items }) => {
  const input = { operationType };

  if (operationType === bulkOverrideOperation.NDC_UPDATE) {
    input.productId = productId;
    input.ndcOverrides = items.map(({ lineItemId, ndc }) => ({ lineItemId, ndc }));
  }

  if (operationType === bulkOverrideOperation.PRODUCT_ID_UPDATE) {
    input.productIdOverrides = items.map((item) => ({
      lineItemId: item.lineItemId,
      productId: item.productId,
      ndc: item.ndc,
      quantity: item.quantity,
    }));
  }

  if (operationType === bulkOverrideOperation.NEEDS_REVIEW_UPDATE) {
    input.needsReviewOverrides = items.map(({ lineItemId, reviewTarget }) =>
      reviewTarget ? { lineItemId, reviewTarget } : { lineItemId }
    );
  }

  if (operationType === bulkOverrideOperation.UNFULFILLMENT_UPDATE) {
    input.unfulfillmentOverrides = items.map(({ lineItemId }) => ({ lineItemId }));
  }

  /* Number(), not the raw field: the grid's editor hands back a string, and the schema types
   * quantity as Float! — a string there is rejected by the server as a variable coercion error
   * rather than by any validation the operator can act on. */
  if (operationType === bulkOverrideOperation.QUANTITY_UPDATE) {
    input.quantityOverrides = items.map(({ lineItemId, quantity }) => ({
      lineItemId,
      quantity: Number(quantity),
    }));
  }

  /* Already shaped by splitItem (utils/bulkOverrideStaging.js) as the split API takes it:
   * { orderId, lineItemId, splits: [{ splitQuantity, scheduledFor }] }. Picked apart and rebuilt
   * rather than spread so a stray staging key can never reach the mutation -- the input is
   * strongly typed server-side and an unknown field is a request-wide GraphQL error, not a
   * per-row one.
   *
   * scheduledFor travels PER ROW inside splits, not once at the top: the apply bar sets one date
   * across the selection, but any row can then be moved to a different day inline, so a single
   * batch-wide field would silently discard those edits. */
  if (operationType === bulkOverrideOperation.FILL_LATER_SPLIT) {
    input.splitOverrides = items.map(({ orderId, lineItemId, splits }) => ({
      orderId,
      lineItemId,
      splits,
    }));
  }

  if (operationType === bulkOverrideOperation.FILL_LATER_MOVE) {
    input.moveOverrides = items.map(({ orderId, lineItemId, scheduledFor }) => ({
      orderId,
      lineItemId,
      scheduledFor,
    }));
  }

  /* Returns the whole payload, not just `results`.
   *
   * Above the server's async threshold (50 line items) the work is queued and `results` comes back
   * EMPTY, with the outcome arriving over websocket instead. A caller that reads only `results`
   * would render "0 of 800 updated" for a run that is proceeding perfectly well, so `status` has to
   * be visible to the caller. */
  return graphqlClient
    .request(bulkOverrideLineItemsGql, { input })
    .then(({ bulkOverrideLineItems }) => bulkOverrideLineItems);
};
